import { createContext, type ReactNode, useContext } from 'react';
import type { FindingRefsLookup } from './useFindingRefs';

/**
 * Reference labels for the current run, resolved once at the page and read by `FindingText` /
 * `ReferenceChip` wherever a finding is rendered — three levels deep (card -> description/evidence
 * -> field grid), so this is a context rather than a prop threaded through all of them.
 */
const FindingRefsContext = createContext<FindingRefsLookup | undefined>(undefined);

export const FindingRefsProvider = ({
  refs,
  children,
}: {
  refs: FindingRefsLookup;
  children: ReactNode;
}) => <FindingRefsContext.Provider value={refs}>{children}</FindingRefsContext.Provider>;

/**
 * Falls back to an always-unresolved lookup outside a provider (e.g. a component test rendering
 * `FindingText` in isolation), so every reference renders its plain fallback label rather than the
 * hook throwing.
 */
const UNRESOLVED: FindingRefsLookup = {
  item: () => undefined,
  objective: () => undefined,
  team: () => undefined,
};

export const useFindingRefsLookup = (): FindingRefsLookup =>
  useContext(FindingRefsContext) ?? UNRESOLVED;
